import { useEffect, useState } from 'react';
import { getDealerPriceAndAvailability } from '@app/store/checkout/actions';
import {
  ADD_TO_CART_MODIFICATIONS_RESET,
  GET_DEALER_PRICE_AND_AVAILABILITY_RESET,
  RESET_CURRENT_MODAL_PHASE,
  UPDATE_PARTS
} from '@app/store/checkout/constants';
import {
  AlloyButton,
  ChildModal,
  ComponentLoader,
  StickySection,
  useModal
} from 'cat-ecommerce-alloy';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ReplacementPartsModal from '../ReplacementPartsModal';
import AlternativePartsModal from '../AlternativePartsModal';
import SOSTable from './SOSTable';
import styles from './styles.module.scss';
import QOStyles from '../styles.module.scss';
import { STATUS } from '@app/constants/commonConstants';
import {
  ERROR_DOMAIN,
  ERROR_PATH,
  UNKNOWNERROR,
  UNKNOWN_ERROR_CANCEL_IMPORT
} from '@app/constants/errorConstants';
import { clearError, setError } from '@app/store/exception/actions';
import { Form, Formik, useFormikContext } from 'formik';
import { getItemsWithRadioValues, getSelectedSOSItems } from './utils';
import {
  setInvalidQuantityErrors,
  mergeRepeatedQOValuesAndValidate
} from '../utils';
import { useValidateQuantity } from '../hooks';
import ExceptionFeedbackModal from '../common/ExceptionFeedbackModal/ExceptionFeedbackModal';
import PreferenceToggle from '../common/PreferenceToggle';
import Disclaimers from '@app/components/pages/checkout/shopping-cart/QuickOrder/common/Disclaimers';
import { ALTERNATIVE, REPLACEMENT } from '../../ShoppingCartPage/constants';

const SOSModal = ({
  modalOptions,
  isAvailabilityToggled,
  setIsAvailabilityToggled
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const childModalOptions = useModal({
    isChild: true,
    parentIsOpen: modalOptions.isOpen
  });

  const { validateItemQuantity } = useValidateQuantity();

  const { values: quickOrderValues, setErrors: setQOErrors } =
    useFormikContext();

  const items = useSelector(
    s => s.checkout.currentAddToCartModifications.items
  );
  const hasAlternatives = useSelector(
    s => s.checkout.currentAddToCartHasAlternate
  );
  const hasReplacements = useSelector(
    s => s.checkout.currentAddToCartHasReplacement
  );
  const dealerPriceAndAvailabilityStatus = useSelector(
    s => s.checkout.dealerPriceAndAvailabilityStatus
  );
  const addToCartStatus = useSelector(s => s.checkout.addToCartStatus);

  const currentModalPhase = useSelector(s => s.checkout.currentModalPhase);

  const isDealerPriceAndAvailabilityLoading =
    dealerPriceAndAvailabilityStatus === STATUS.PENDING;
  const isAddToCartStatusLoading = addToCartStatus === STATUS.PENDING;
  const isFetchingInfo =
    isAddToCartStatusLoading || isDealerPriceAndAvailabilityLoading;

  const initialRadioButtonValues = Object.keys(items).reduce((acc, key) => {
    const part = items[key];
    return {
      ...acc,
      // Passing part.selectedSOS as a string value because 0 values when converted to boolean show as falsy
      // and Radio alloy component uses boolean conversion. Using a string avoids this issue.
      [part.referenceId]: `${part.selectedSOS}`
    };
  }, {});
  const initialValues = { ...initialRadioButtonValues };

  const onSubmit = values => {
    const itemsToSend = getItemsWithRadioValues(items, values);
    dispatch({ type: UPDATE_PARTS, payload: itemsToSend });

    // Returns true only if there is a quantity error and the part does not have any replacements or alternatives
    const callback = ({ alternateParts, replacementParts }) => {
      const itemsToQuantityValidate = getSelectedSOSItems(itemsToSend);
      const quantityErrors = mergeRepeatedQOValuesAndValidate(
        itemsToQuantityValidate,
        validateItemQuantity
      );
      const errorsWithNoAltOrReplParts = quantityErrors.filter(
        errorId =>
          !replacementParts.some(id => id.referenceId === errorId) &&
          !alternateParts.some(item => item.originalPartReferenceId === errorId)
      );
      if (
        !errorsWithNoAltOrReplParts.length &&
        (alternateParts?.length || replacementParts?.length)
      ) {
        childModalOptions.open();
      } else if (errorsWithNoAltOrReplParts.length) {
        dispatch({ type: GET_DEALER_PRICE_AND_AVAILABILITY_RESET });
        dispatch({ type: ADD_TO_CART_MODIFICATIONS_RESET });
        setInvalidQuantityErrors(
          errorsWithNoAltOrReplParts,
          { items: Object.values(quickOrderValues.items) },
          setQOErrors,
          t
        );
        dispatch(
          setError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.MAIN, {
            title: t('UNKNOWN_ERROR_TITLE'),
            message: t('QO_MAXIMUM_QUANTITY_UPDATED_ERROR')
          })
        );
      }
      return !!errorsWithNoAltOrReplParts.length;
    };
    const errorInfo = modalOptions.isOpen
      ? {
          domain: ERROR_DOMAIN.QUICK_ORDER,
          path: ERROR_PATH.ADD_TO_CART,
          modalError: {
            message: UNKNOWN_ERROR_CANCEL_IMPORT,
            title: UNKNOWNERROR.title
          }
        }
      : {};
    dispatch(getDealerPriceAndAvailability(errorInfo, callback));
  };

  const [originalItems] = useState(items);

  /**
   * Resets currentAddToCartModifications to original values if user has
   * returned to SOS modal from child modals (i.e. Alternate or replacement parts modals)
   */
  useEffect(() => {
    if (!childModalOptions.isOpen) {
      dispatch({ type: UPDATE_PARTS, payload: originalItems });
    }
  }, [childModalOptions.isOpen, dispatch, originalItems]);

  const handleChildModalClose = () => {
    dispatch(clearError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.ADD_TO_CART));
    dispatch({ type: RESET_CURRENT_MODAL_PHASE });
    childModalOptions.close();
  };

  useEffect(() => {
    if (
      currentModalPhase === REPLACEMENT ||
      currentModalPhase === ALTERNATIVE
    ) {
      childModalOptions.open();
    }
  }, [currentModalPhase]);

  const onCancel = () => {
    dispatch({ type: GET_DEALER_PRICE_AND_AVAILABILITY_RESET });
    dispatch({ type: ADD_TO_CART_MODIFICATIONS_RESET });
    dispatch(clearError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.ADD_TO_CART));
    modalOptions.close();
  };
  const errorSelector = state =>
    state.errors[ERROR_DOMAIN.QUICK_ORDER]?.[ERROR_PATH.ADD_TO_CART];

  const error = useSelector(errorSelector);

  return (
    <>
      {isFetchingInfo && <ComponentLoader contentClassName="position-sticky" />}
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        <Form name="sos-modal-form">
          <ExceptionFeedbackModal
            tryAgainButtonProps={{ type: 'submit' }}
            errorSelector={errorSelector}
            onCancelImportButton={onCancel}
          />
          <p className="u-color--DarkGray u-fs--14 u-fs-md--16 mb-3 mb-lg-2">
            {t('SOS_MODAL_SUBTITLE')}
          </p>
          <PreferenceToggle
            id="SOSModal"
            isAvailabilityToggled={isAvailabilityToggled}
            setIsAvailabilityToggled={setIsAvailabilityToggled}
          />
          <div className="pb-3 pt-3 pt-lg-2">
            {Object.keys(originalItems).map(key => {
              const item = originalItems[key];
              const hasMultipleSOS = item?.sosList?.length > 1;
              return hasMultipleSOS ? (
                <div key={item.referenceId}>
                  <h3 className="h2 display-2 u-fs--24">
                    {t('SOS_MSG_ORG_PART')}
                    {item.partNumber} ({item.sosList[0].unitPrice}{' '}
                    {item.currency})
                  </h3>
                  <div className={cx('mb-3', styles['table-border'])}>
                    <SOSTable
                      currency={item.currency}
                      referenceId={item.referenceId}
                      requestedQuantity={item.requestedQuantity}
                      sosOptions={item.sosList}
                      isAvailabilityToggled={isAvailabilityToggled}
                    />
                  </div>
                </div>
              ) : null;
            })}
          </div>
          <Disclaimers />
          <StickySection
            className={cx(
              QOStyles['sticky-section'],
              'd-flex justify-content-between mt-3 px-3 py-3 align-items-center bg-white z-index-1 shadow rounded'
            )}
          >
            <AlloyButton
              buttonType="ghost"
              onClick={onCancel}
              disabled={isFetchingInfo}
            >
              {t('CANCEL')}
            </AlloyButton>
            <AlloyButton
              buttonType="primary"
              type="submit"
              disabled={isFetchingInfo || !!error}
            >
              {t('SHOPCART_UPDATE')}
            </AlloyButton>
          </StickySection>
        </Form>
      </Formik>
      {hasReplacements && (
        <ChildModal
          title={t('QUICK_ORDER_REPLACEMENT_PARTS')}
          {...childModalOptions}
          backButtonMessage={t('BACK_TO_SOURCE_OF_SUPPLY_BREADCRUMB')}
          close={handleChildModalClose}
        >
          <ReplacementPartsModal
            modalOptions={childModalOptions}
            isChildModal
            isAvailabilityToggled={isAvailabilityToggled}
            setIsAvailabilityToggled={setIsAvailabilityToggled}
          />
        </ChildModal>
      )}
      {!hasReplacements && hasAlternatives && (
        <ChildModal
          title={t('ALTERNATIVE_PARTS')}
          {...childModalOptions}
          backButtonMessage={t('BACK_TO_SOURCE_OF_SUPPLY_BREADCRUMB')}
          close={handleChildModalClose}
        >
          <AlternativePartsModal
            modalOptions={childModalOptions}
            isChildModal
            isAvailabilityToggled={isAvailabilityToggled}
            setIsAvailabilityToggled={setIsAvailabilityToggled}
          />
        </ChildModal>
      )}
    </>
  );
};

SOSModal.propTypes = {
  isAvailabilityToggled: PropTypes.bool,
  setIsAvailabilityToggled: PropTypes.func,
  modalOptions: PropTypes.shape({
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func
  })
};
export default SOSModal;
